import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Project, ProjectAsset, apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Trash2, Eye, Upload, FileImage, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface AdminProjectAssetsSectionProps {
    project: Project;
    onUpdate: () => void;
}

export function AdminProjectAssetsSection({ project, onUpdate }: AdminProjectAssetsSectionProps) {
    const { toast } = useToast();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isUploading, setIsUploading] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    // Dialog States
    const [showUploadDialog, setShowUploadDialog] = useState(false);
    const [fileToUpload, setFileToUpload] = useState<File | null>(null);
    const [uploadReason, setUploadReason] = useState("");

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [assetToDelete, setAssetToDelete] = useState<ProjectAsset | null>(null);
    const [deleteReason, setDeleteReason] = useState("");

    const fileInputRef = useRef<HTMLInputElement>(null);

    const activeAssets = project.assets || [];

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 50 * 1024 * 1024) { // Increased limit to 50MB for admins
            toast({
                title: "File too large",
                description: "Asset size must be less than 50MB.",
                variant: "destructive",
            });
            return;
        }

        setFileToUpload(file);
        setShowUploadDialog(true);
    };

    const handleConfirmUpload = async () => {
        if (!fileToUpload) return;
        if (!uploadReason.trim()) {
            toast({
                title: "Reason required",
                description: "Please provide a reason for this override.",
                variant: "destructive",
            });
            return;
        }

        setIsUploading(true);
        try {
            await apiClient.uploadProjectAssetOverride(project._id, fileToUpload, uploadReason);
            toast({
                title: "Asset uploaded",
                description: "The asset has been successfully uploaded and a score recalculation triggered.",
                variant: "default",
                className: "border-green-200 bg-green-50 text-green-800",
            });
            setShowUploadDialog(false);
            setFileToUpload(null);
            setUploadReason("");
            onUpdate();
            queryClient.invalidateQueries({ queryKey: ["adminProjects"] });
            navigate("/admin/projects");
        } catch (error) {
            toast({
                title: "Upload failed",
                description: error instanceof Error ? error.message : "An error occurred during upload.",
                variant: "destructive",
            });
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleDeleteClick = (asset: ProjectAsset) => {
        setAssetToDelete(asset);
        setShowDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (!assetToDelete) return;
        if (!deleteReason.trim()) {
            toast({
                title: "Reason required",
                description: "Please provide a reason for removing this asset.",
                variant: "destructive",
            });
            return;
        }

        setDeletingId(assetToDelete.id);
        try {
            await apiClient.deleteProjectAssetOverride(project._id, assetToDelete.id, deleteReason);
            toast({
                title: "Asset deleted",
                description: "The asset has been removed and a score recalculation triggered.",
            });
            setShowDeleteDialog(false);
            setAssetToDelete(null);
            setDeleteReason("");
            onUpdate();
            queryClient.invalidateQueries({ queryKey: ["adminProjects"] });
            navigate("/admin/projects");
        } catch (error) {
            console.error("Delete failed:", error);
            toast({
                title: "Delete failed",
                description: error instanceof Error ? error.message : "Failed to delete asset.",
                variant: "destructive",
            });
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Project Assets (Admin Override)</h3>
                <div>
                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        onChange={handleFileChange}
                    />
                    <Button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        variant="outline"
                        className="flex items-center gap-2"
                    >
                        {isUploading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Upload className="h-4 w-4" />
                        )}
                        Upload Asset Override
                    </Button>
                </div>
            </div>

            {activeAssets.length === 0 ? (
                <div className="text-center p-8 border border-dashed rounded-lg text-muted-foreground bg-muted/50">
                    <FileImage className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No assets uploaded yet.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {activeAssets.map((asset) => (
                        <Card key={asset.id} className="overflow-hidden group">
                            <div className="aspect-video relative bg-muted flex items-center justify-center overflow-hidden">
                                {asset.content_type?.startsWith("image/") ? (
                                    <img
                                        src={asset.url}
                                        alt={asset.filename}
                                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = "";
                                            (e.target as HTMLImageElement).style.display = 'none';
                                        }}
                                    />
                                ) : null}
                                <div className="absolute inset-0 flex items-center justify-center -z-10">
                                    <FileImage className="h-8 w-8 text-muted-foreground/50" />
                                </div>

                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                    <Button
                                        size="icon"
                                        variant="secondary"
                                        onClick={() => window.open(asset.url, '_blank')}
                                        title="View Full Size"
                                    >
                                        <Eye className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        size="icon"
                                        variant="destructive"
                                        onClick={() => handleDeleteClick(asset)}
                                        disabled={deletingId === asset.id}
                                        title="Delete Asset"
                                    >
                                        {deletingId === asset.id ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Trash2 className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                            <CardContent className="p-3">
                                <div className="text-sm font-medium truncate" title={asset.original_filename || asset.filename}>
                                    {asset.original_filename || asset.filename}
                                </div>
                                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                                    <span>{asset.width > 0 ? `${asset.width}x${asset.height}` : "File"}</span>
                                    <span>{(asset.size / 1024).toFixed(0)} KB</span>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Upload Confirmation/Reason Dialog */}
            <AlertDialog open={showUploadDialog} onOpenChange={(open) => {
                setShowUploadDialog(open);
                if (!open) {
                    setFileToUpload(null);
                    setUploadReason("");
                    if (fileInputRef.current) fileInputRef.current.value = "";
                }
            }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Upload Asset Override</AlertDialogTitle>
                        <AlertDialogDescription>
                            You are about to upload "{fileToUpload?.name}". This action will be logged and will trigger an automatic Artie Score recalculation.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="my-4">
                        <Label htmlFor="upload-reason" className="mb-2 block font-medium">
                            Reason for Update <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                            id="upload-reason"
                            placeholder="e.g., Creator emailed the final assets today"
                            value={uploadReason}
                            onChange={(e) => setUploadReason(e.target.value)}
                        />
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isUploading}>Cancel</AlertDialogCancel>
                        <Button
                            onClick={handleConfirmUpload}
                            disabled={isUploading || !uploadReason.trim()}
                        >
                            {isUploading ? "Uploading..." : "Upload & Recalculate"}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Confirmation/Reason Dialog */}
            <AlertDialog open={showDeleteDialog} onOpenChange={(open) => {
                setShowDeleteDialog(open);
                if (!open) {
                    setAssetToDelete(null);
                    setDeleteReason("");
                }
            }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove Asset Override</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to remove "{assetToDelete?.original_filename || assetToDelete?.filename}"?
                            This action will be logged and will trigger an automatic Artie Score recalculation.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="my-4">
                        <Label htmlFor="delete-reason" className="mb-2 block font-medium">
                            Reason for Removal <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                            id="delete-reason"
                            placeholder="e.g., Asset is no longer relevant for this project"
                            value={deleteReason}
                            onChange={(e) => setDeleteReason(e.target.value)}
                        />
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deletingId === assetToDelete?.id}>
                            Cancel
                        </AlertDialogCancel>
                        <Button
                            onClick={handleConfirmDelete}
                            disabled={deletingId === assetToDelete?.id || !deleteReason.trim()}
                            variant="destructive"
                        >
                            {deletingId === assetToDelete?.id ? "Removing..." : "Remove & Recalculate"}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
